import React from 'react';
import { Sparkles, Moon, Heart, Leaf, Shield, Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { aboutContent } from '../mockData';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Sparkles className="w-16 h-16 mx-auto mb-6 text-yellow-400 animate-pulse" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">About Infused by Daizy</h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Crafting magically charged ritual oils with intention, love, and ancient wisdom
          </p>
        </div>

        {/* Meet the Creator Section */}
        <div className="mb-16">
          <Card className="bg-purple-800/40 border-purple-600 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Photo */}
              <div className="relative h-[500px] lg:h-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1984c3e1-4b2c-4786-b54b-12533e8a13f2/artifacts/a4x7hp7s_PSX_20260212_213029.jpg" 
                  alt="Daizy - Creator of Infused by Daizy"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Text */}
              <div className="flex flex-col justify-center p-8 lg:p-12 bg-purple-900/60">
                <h2 className="text-4xl font-bold text-yellow-400 mb-4">Meet Daizy</h2>
                <p className="text-lg text-purple-100 leading-relaxed mb-6">
                  Hello, beautiful soul! I&apos;m Daizy, the heart and hands behind Infused by Daizy. My journey into creating sacred intention oils began through my own spiritual awakening and desire to share healing energy with others.
                </p>
                <p className="text-lg text-purple-100 leading-relaxed mb-6">
                  Each oil I create is infused with love, intention, and the anointing of divine energies. I believe that self-care is a sacred practice, and these oils are designed to support you on your journey of self-love, peace, and spiritual growth.
                </p>
                <p className="text-lg text-purple-100 leading-relaxed italic">
                  &ldquo;Sacred Drops of Love and Light&rdquo;
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Mission Section */}
        <div className="mb-16">
          <Card className="bg-purple-800/50 border-purple-600">
            <CardContent className="pt-8 pb-8">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">Our Mission</h2>
              <p className="text-lg text-purple-100 leading-relaxed text-center max-w-4xl mx-auto">
                {aboutContent.mission}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Story Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <Card className="bg-purple-800/50 border-purple-600">
            <CardContent className="pt-8 pb-8">
              <h2 className="text-3xl font-bold text-white mb-6">Our Story</h2>
              <p className="text-lg text-purple-100 leading-relaxed mb-6">
                {aboutContent.story}
              </p>
              <p className="text-lg text-purple-100 leading-relaxed">
                We use only the finest ingredients, ethically sourced and sustainably harvested. Each bottle is a labor of love, created in small batches to ensure the highest quality and energetic potency.
              </p>
            </CardContent>
          </Card>

          <div className="relative">
            <Card className="bg-purple-800/40 border-purple-600 overflow-hidden h-full">
              <img 
                src="https://images.unsplash.com/photo-1567113463224-37cf03ba4577" 
                alt="Crystals on wood"
                className="w-full h-full object-cover"
              />
            </Card>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-white mb-8 text-center">What Makes Us Special</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aboutContent.values.map((value, index) => {
              const icons = [Moon, Heart, Sparkles, Leaf, Shield, Star];
              const Icon = icons[index % icons.length];
              
              return (
                <Card key={index} className="bg-purple-800/50 border-purple-600 hover:border-yellow-500 transition-all duration-300">
                  <CardContent className="pt-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
                      <Icon className="w-8 h-8 text-yellow-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">{value}</h3>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Process Section */}
        <Card className="bg-purple-800/50 border-purple-600">
          <CardContent className="pt-8 pb-8">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Our Sacred Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-purple-700 rounded-full flex items-center justify-center text-yellow-400 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Intention Setting</h3>
                <p className="text-purple-200">
                  We begin by setting clear intentions during meditation, aligning with the moon phase for maximum potency.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-purple-700 rounded-full flex items-center justify-center text-yellow-400 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Hand Blending</h3>
                <p className="text-purple-200">
                  Each oil is carefully hand-blended with premium natural ingredients, sacred herbs, and crystal essences.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-purple-700 rounded-full flex items-center justify-center text-yellow-400 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Energy Charging</h3>
                <p className="text-purple-200">
                  Oils are charged with Reiki energy and anointed under moonlight, sealed in glass bottles for protection.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default About;
